import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", company: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      toast.success("Welcome to Runwell HQ.");
      navigate("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-up failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8] flex">
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-[#E54D2E] text-white p-12 relative grain">
        <Link to="/" className="flex items-center gap-2" data-testid="register-brand-link">
          <div className="w-5 h-5 bg-white" />
          <span className="font-display text-lg font-bold">Runwell HQ</span>
        </Link>
        <div>
          <div className="mono text-xs opacity-80 mb-4">— FIRST 30 DAYS FREE</div>
          <h1 className="font-display text-5xl font-bold leading-tight">
            Install a real operating system in one afternoon.
          </h1>
          <p className="mt-6 opacity-90 max-w-md">Vision. Scorecard. Rocks. Issues. Meetings. Accountability. Processes. And an AI coach who's read every ops book.</p>
        </div>
        <div className="mono text-xs opacity-70">Trusted by operators of 10–100 person teams.</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-5">
          <div>
            <div className="mono text-xs tracking-widest uppercase text-neutral-500">CREATE ACCOUNT</div>
            <h2 className="font-display text-4xl font-bold mt-2">Start your OS.</h2>
          </div>
          <div className="space-y-2">
            <Label htmlFor="name">Your name</Label>
            <Input id="name" required value={form.name} onChange={set("name")} className="rounded-sm" data-testid="register-name-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="company">Company</Label>
            <Input id="company" required value={form.company} onChange={set("company")} className="rounded-sm" data-testid="register-company-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={form.email} onChange={set("email")} className="rounded-sm" data-testid="register-email-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} className="rounded-sm" data-testid="register-password-input" />
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-neutral-900 hover:bg-neutral-800 text-white rounded-sm btn-lift" data-testid="register-submit-btn">
            {busy ? "Creating…" : "Create account"}
          </Button>
          <div className="text-sm text-neutral-600">
            Have an account? <Link to="/login" className="text-[#E54D2E] font-medium" data-testid="register-login-link">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
